/* eslint-disable react-refresh/only-export-components -- utility function co-located with components for cohesion */
import { cn } from '@/components/ui/cn';

/**
 * Quiet Atlas illustration kit — thin vector ornaments in the 1970s
 * topographic atlas tradition. All assets live in `/public/atlas/` and ship
 * as static SVG; this module just wraps them with consistent sizing & a11y.
 */

// Generated by web/scripts/generate-silhouettes.mjs — kept in sync with
// the SVGs in /public/atlas/silhouette-*.svg. All 30 COUNTRY_CODES covered.
const SILHOUETTE_AVAILABLE = new Set([
  'AE', 'PT', 'ES', 'CY', 'TH', 'MY', 'ID', 'GR', 'MU', 'IT',
  'CR', 'MT', 'PA', 'EE', 'ME', 'RS', 'GE', 'MX', 'SG', 'UY',
  'AL', 'HU', 'BR', 'PY', 'VN', 'AR', 'TR', 'HR', 'ZA', 'CO',
]);

export function hasSilhouette(code: string): boolean {
  return SILHOUETTE_AVAILABLE.has(code);
}

export interface CompassRoseProps {
  className?: string;
  size?: number;
}

export function CompassRose({ className, size = 56 }: CompassRoseProps) {
  return (
    <img
      src="/atlas/compass.svg"
      alt=""
      aria-hidden="true"
      width={size}
      height={size}
      className={cn('pointer-events-none select-none', className)}
    />
  );
}

export interface CountrySilhouetteProps {
  code: 'IT' | 'PT' | 'ES' | 'AE' | 'CY' | string;
  className?: string;
  width?: number;
  height?: number;
}

/**
 * Renders a country silhouette outline, or nothing if the silhouette asset
 * isn't shipped for that country yet.
 */
export function CountrySilhouette({
  code,
  className,
  width = 64,
  height,
}: CountrySilhouetteProps) {
  if (!hasSilhouette(code)) return null;
  return (
    <img
      src={`/atlas/silhouette-${code.toLowerCase()}.svg`}
      alt=""
      aria-hidden="true"
      width={width}
      height={height}
      className={cn('pointer-events-none select-none', className)}
    />
  );
}

export interface ContourPatchProps {
  className?: string;
  /** 0–1, default 0.18 — overrides the SVG's internal opacity. */
  opacity?: number;
}

export function ContourPatch({ className, opacity = 0.18 }: ContourPatchProps) {
  return (
    <img
      src="/atlas/contour-patch.svg"
      alt=""
      aria-hidden="true"
      className={cn('pointer-events-none select-none', className)}
      style={{ opacity }}
    />
  );
}

export interface TickDividerProps {
  /** Left label, e.g. '45°N'. */
  left?: string;
  /** Right label, e.g. '11°E'. */
  right?: string;
  className?: string;
}

/**
 * Thin lat/lon tick-mark divider for the marketing landing. Used between
 * sections in place of <hr>.
 */
export function TickDivider({ left, right, className }: TickDividerProps) {
  return (
    <div
      role="separator"
      aria-hidden="true"
      className={cn(
        'mx-auto flex max-w-5xl items-center gap-4 px-6 py-4 font-mono text-[11px] uppercase tracking-[0.15em] text-ink-400',
        className,
      )}
    >
      {left && <span>{left}</span>}
      <span className="relative h-px flex-1 bg-ink-100">
        <span className="absolute left-0 -top-[3px] h-[7px] w-px bg-ink-300" />
        <span className="absolute right-0 -top-[3px] h-[7px] w-px bg-ink-300" />
      </span>
      {right && <span>{right}</span>}
    </div>
  );
}

/**
 * Monthly mean temperature sparkline for Italy.
 * Numbers approximate WMO 1991–2020 normals for a central-Italy reference;
 * not exact data — purely a decorative live-data signal in the hero.
 */
export interface MonthlySparklineProps {
  /** 12 numbers, °C. Defaults to Italy reference series. */
  monthly?: number[];
  /** Country code shown in the label. */
  countryCode?: string;
  /** Source attribution shown next to the label. */
  source?: string;
  className?: string;
}

const ITALY_MONTHLY_T = [5, 6, 9, 12, 17, 21, 24, 24, 20, 15, 10, 6];

export function MonthlySparkline({
  monthly = ITALY_MONTHLY_T,
  countryCode = 'IT',
  source = 'Open-Meteo · 1991–2020',
  className,
}: MonthlySparklineProps) {
  const width = 320;
  const height = 60;
  const padding = 6;
  const min = Math.min(...monthly);
  const max = Math.max(...monthly);
  const range = max - min || 1;

  const step = (width - padding * 2) / (monthly.length - 1);
  const points = monthly
    .map((v, i) => {
      const x = padding + i * step;
      const t = (v - min) / range;
      const y = padding + (1 - t) * (height - padding * 2 - 12);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  const maxIdx = monthly.indexOf(max);
  const minIdx = monthly.indexOf(min);
  const dot = (i: number) => {
    const [x, y] = points.split(' ')[i].split(',');
    return { x, y };
  };
  const maxDot = dot(maxIdx);
  const minDot = dot(minIdx);

  const months = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

  return (
    <figure
      className={cn('border-t border-ink-100 pt-4', className)}
      aria-label={`${countryCode} annual temperature — ${source}`}
    >
      <figcaption className="font-mono text-[11px] uppercase tracking-wider text-ink-500">
        {countryCode} · annual t° · {source}
      </figcaption>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="mt-2 block max-w-full"
        role="img"
        aria-hidden="true"
      >
        <polyline
          fill="none"
          stroke="var(--color-terra-600)"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={points}
        />
        <circle cx={maxDot.x} cy={maxDot.y} r="2.5" fill="var(--color-terra-600)" />
        <circle cx={minDot.x} cy={minDot.y} r="2.5" fill="var(--color-terra-600)" />
        <line
          x1="0"
          y1={height - 2}
          x2={width}
          y2={height - 2}
          stroke="var(--color-ink-100)"
          strokeWidth="0.6"
        />
      </svg>
      <div className="mt-1 flex justify-between font-mono text-[9px] uppercase tracking-[0.08em] text-ink-400">
        {months.map((m, i) => (
          <span key={i}>{m}</span>
        ))}
      </div>
    </figure>
  );
}
